import { useEffect, useRef, DependencyList } from 'react'

/**
 * Custom hook that only runs the effect when the screen matches desktop media query
 * @param effect - The effect function to run
 * @param deps - Dependencies array for the effect
 */
export const useDesktopEffect = (effect: () => void | (() => void), deps?: DependencyList) => {
  const mediaQueryRef = useRef<MediaQueryList | null>(null)
  const cleanupRef = useRef<(() => void) | void>(void 0)

  useEffect(() => {
    // Create media query for desktop (typically 768px and up)
    mediaQueryRef.current = window.matchMedia('(min-width: 768px)')
    
    const handleMediaChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        // Desktop: run the effect
        const cleanup = effect()
        if (typeof cleanup === 'function') {
          cleanupRef.current = cleanup
        }
      } else {
        // Mobile: cleanup if there's a cleanup function
        if (cleanupRef.current) {
          cleanupRef.current()
          cleanupRef.current = void 0
        }
      }
    }

    // Check initial state
    if (mediaQueryRef.current.matches) {
      const cleanup = effect()
      if (typeof cleanup === 'function') {
        cleanupRef.current = cleanup
      }
    }

    // Listen for media query changes
    mediaQueryRef.current.addEventListener('change', handleMediaChange)

    // Cleanup function
    return () => {
      if (mediaQueryRef.current) {
        mediaQueryRef.current.removeEventListener('change', handleMediaChange)
      }
      if (cleanupRef.current) {
        cleanupRef.current()
      }
    }
  }, deps)
}
